import { useState, useEffect, useRef } from 'react'
import { X, Search, ChevronDown, Check } from 'lucide-react'
import { useTranslation } from '../i18n/LanguageContext'
import { useThemeColor } from '../hooks/useThemeColor'
import { BASE_SHARES_URL } from './constants'
import { useToast } from './Toast'

const Avatar = ({ name, email, size = 32, color }) => {
  const letter = (name || email || '?').charAt(0).toUpperCase()
  return (
    <div
      className="rounded-full flex items-center justify-center font-semibold flex-shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.4, backgroundColor: `${color}20`, color }}
    >
      {letter}
    </div>
  )
}

const ShareModal = ({ isOpen, onClose, historyId, projectName, currentUser, onInviteSent }) => {
  const { t } = useTranslation()
  const { primaryColor } = useThemeColor()
  const { addToast } = useToast()

  // ── view: 'main' | 'allViewers' ─────────────────────────────────────────────
  const [view, setView]                   = useState('main')

  // ── invite ───────────────────────────────────────────────────────────────────
  const [query, setQuery]                 = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [selectedUsers, setSelectedUsers] = useState([])
  const [isSearching, setIsSearching]     = useState(false)
  const [isSending, setIsSending]         = useState(false)
  const [sendError, setSendError]         = useState('')
  const [searchFocused, setSearchFocused] = useState(false)

  // ── recipients ───────────────────────────────────────────────────────────────
  const [recipients, setRecipients]       = useState([])
  const [loadingRecipients, setLoadingRecipients] = useState(false)
  const [removingEmail, setRemovingEmail] = useState(null)
  const [openPermMenu, setOpenPermMenu]   = useState(null) // email whose menu is open

  // ── all-viewers search ───────────────────────────────────────────────────────
  const [viewerSearch, setViewerSearch]   = useState('')

  const searchTimeout = useRef(null)
  const permMenuRef   = useRef(null)
  const token   = localStorage.getItem('token')
  const userId  = localStorage.getItem('id')

  // ── reset on open/close ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) {
      setView('main')
      setQuery('')
      setSearchResults([])
      setSelectedUsers([])
      setSendError('')
      setIsSending(false)
      setRecipients([])
      setOpenPermMenu(null)
      setViewerSearch('')
      return
    }
    if (historyId) {
      setLoadingRecipients(true)
      fetch(`${BASE_SHARES_URL}/recipients/${historyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() : { recipients: [] })
        .then(d => setRecipients(d.recipients || []))
        .catch(() => setRecipients([]))
        .finally(() => setLoadingRecipients(false))
    }
  }, [isOpen, historyId, token])

  // ── close perm menu on outside click ────────────────────────────────────────
  useEffect(() => {
    if (!openPermMenu) return
    const handler = (e) => {
      if (permMenuRef.current && !permMenuRef.current.contains(e.target)) {
        setOpenPermMenu(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [openPermMenu])

  // ── debounced search ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!query.trim()) { setSearchResults([]); setIsSearching(false); clearTimeout(searchTimeout.current); return }
    setIsSearching(true)
    clearTimeout(searchTimeout.current)
    searchTimeout.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `${BASE_SHARES_URL}/search-users?q=${encodeURIComponent(query)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        setSearchResults(res.ok ? (await res.json()).users || [] : [])
      } catch { setSearchResults([]) }
      finally { setIsSearching(false) }
    }, 300)
    return () => clearTimeout(searchTimeout.current)
  }, [query, token])

  const toggleUser = (user) => {
    // Block adding users who are already shared
    if (recipients.some(r => r.recipient_email === user.user_email)) return
    setSelectedUsers(prev =>
      prev.find(u => u.user_email === user.user_email)
        ? prev.filter(u => u.user_email !== user.user_email)
        : [...prev, user]
    )
    setQuery('')
    setSearchResults([])
  }

  const refreshRecipients = () => {
    if (!historyId) return
    fetch(`${BASE_SHARES_URL}/recipients/${historyId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { recipients: [] })
      .then(d => setRecipients(d.recipients || []))
      .catch(() => {})
  }

  const handleSendInvite = async () => {
    const existingEmails = new Set(recipients.map(r => r.recipient_email))
    const newUsers = selectedUsers.filter(u => !existingEmails.has(u.user_email))
    if (!newUsers.length) return
    setIsSending(true)
    setSendError('')
    try {
      const res = await fetch(`${BASE_SHARES_URL}/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          history_id: historyId,
          project_name: projectName || 'Projekt',
          shared_by_user_id: userId,
          recipient_emails: newUsers.map(u => u.user_email),
        }),
      })
      if (res.ok) {
        setSelectedUsers([])
        refreshRecipients()
        addToast(t('share.shareSuccess'), 'success')
        window.dispatchEvent(new CustomEvent('sharesUpdated'))
        onInviteSent?.()
      } else {
        const err = await res.json().catch(() => ({}))
        setSendError(err.detail || `Fehler ${res.status}`)
        addToast(err.detail || `Fehler ${res.status}`, 'error')
      }
    } catch { setSendError('Verbindungsfehler – bitte erneut versuchen.'); addToast('Verbindungsfehler – bitte erneut versuchen.', 'error') }
    finally { setIsSending(false) }
  }

  const handleRemoveRecipient = async (email) => {
    setRemovingEmail(email)
    setOpenPermMenu(null)
    try {
      const res = await fetch(
        `${BASE_SHARES_URL}/recipients/${historyId}/${encodeURIComponent(email)}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      )
      if (!res.ok) {
        // The server refused the removal — 404 when the history isn't ours,
        // 5xx otherwise. Keep the row: dropping it here would tell the owner
        // access was revoked while the recipient still has it. The 404 detail
        // is server-side German, so use the translated string instead.
        addToast(t('share.removeFailed'), 'error')
        return
      }
      setRecipients(prev => {
        const updated = prev.filter(r => r.recipient_email !== email)
        if (updated.length === 0) {
          window.dispatchEvent(new CustomEvent('sharesUpdated'))
          onInviteSent?.()
        }
        return updated
      })
    } catch {
      addToast(t('share.removeFailed'), 'error')
    } finally {
      // In finally, not after the try — the early return above would skip it
      // and leave the row stuck in its removing state.
      setRemovingEmail(null)
    }
  }

  if (!isOpen) return null

  const PREVIEW_COUNT = 3
  const previewRecipients = recipients.slice(0, PREVIEW_COUNT)
  const filteredRecipients = viewerSearch.trim()
    ? recipients.filter(r => r.recipient_email.toLowerCase().includes(viewerSearch.toLowerCase()))
    : recipients
  const totalPeople = recipients.length + 1 // +1 for owner

  // ── shared styles ────────────────────────────────────────────────────────────
  const rowCls = 'flex items-center gap-3 px-4 py-3 border-b border-[#F3F4F6] last:border-0'

  const togglePermMenu = (email) =>
    setOpenPermMenu(prev => (prev === email ? null : email))

  const RecipientRow = ({ r, showMenu, fullLayout = false }) => {
    const isSingleRecipient = recipients.length === 1
    return (
      <div className={rowCls} style={{ backgroundColor: `${primaryColor}08` }}>
        <Avatar name={r.recipient_name || r.recipient_email} email={r.recipient_email} size={36} color={primaryColor} />
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-medium text-[#111827] truncate">{r.recipient_name || r.recipient_email}</p>
          {r.recipient_name && <p className="text-[12px] text-[#9CA3AF] truncate">{r.recipient_email}</p>}
        </div>
        <Check size={14} className="flex-shrink-0 mr-2" style={{ color: primaryColor }} />
        {removingEmail === r.recipient_email ? (
          <svg className="animate-spin h-4 w-4 text-[#9CA3AF]" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
          </svg>
        ) : showMenu ? (
          isSingleRecipient ? (
            <button
              onClick={e => { e.stopPropagation(); handleRemoveRecipient(r.recipient_email); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium text-[#EF4444] border border-[#FECACA] rounded-lg hover:bg-[#FEF2F2] transition-colors flex-shrink-0"
            >
              <X size={12} />
              {t('share.removeAccess')}
            </button>
          ) : (
            <div className="relative" ref={openPermMenu === r.recipient_email ? permMenuRef : null}>
              <button
                onClick={e => { e.stopPropagation(); togglePermMenu(r.recipient_email); }}
                className="flex items-center gap-1 px-3 py-1.5 text-[12px] font-medium text-[#374151] border border-[#E5E7EB] rounded-lg hover:bg-[#F3F4F6] transition-colors"
              >
                {fullLayout ? t('share.viewButton') : t('share.viewPermission')} <ChevronDown size={12} />
              </button>
              {openPermMenu === r.recipient_email && (
                <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-[#E5E7EB] rounded-lg shadow-xl z-[1000] py-1">
                  <div className="flex items-center gap-2 px-3 py-2 text-[13px] text-[#9CA3AF] cursor-default">
                    <Check size={12} /> {t('share.viewPermission')}
                  </div>
                  <div className="border-t border-[#F3F4F6] mx-3 my-1" />
                  <button
                    onClick={e => { e.stopPropagation(); handleRemoveRecipient(r.recipient_email); }}
                    className="w-full text-left px-3 py-2 text-[13px] text-[#EF4444] hover:bg-[#FEF2F2] transition-colors"
                  >
                    {t('share.removeAccess')}
                  </button>
                </div>
              )}
            </div>
          )
        ) : null}
      </div>
    )
  }

  // ════════════════════════════════════════════════════════════════════════════
  //  MAIN VIEW
  // ════════════════════════════════════════════════════════════════════════════
  if (view === 'main') return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[520px] mx-4 max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0">
          <h2 className="text-[18px] font-bold text-[#111827]">{t('share.modalTitle')}</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-[#374151] hover:bg-[#F9FAFB] transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 pb-6 flex-1 overflow-y-auto thin-scrollbar space-y-5">
          <p className="text-[13px] text-[#6B7280] leading-relaxed">{t('share.inviteDescription')}</p>

          {/* ── Invite section ─────────────────────────────────────── */}
          <div>
            <p className="text-[13px] font-semibold text-[#111827] mb-2.5">{t('share.invitePeople')}</p>

            {/* Chips: existing recipients (locked) + newly selected (removable) */}
            {(recipients.length > 0 || selectedUsers.length > 0) && !loadingRecipients && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {/* Already-shared — locked chips with theme color */}
                {recipients.map(r => (
                  <div
                    key={r.recipient_email}
                    className="flex items-center gap-1 pl-2 pr-2 py-0.5 rounded-full text-[12px] font-medium border"
                    style={{ borderColor: primaryColor, color: primaryColor, backgroundColor: `${primaryColor}18` }}
                    title={t('share.alreadyShared')}
                  >
                    <Check size={9} className="flex-shrink-0" />
                    <span className="truncate max-w-[120px]">{r.recipient_name || r.recipient_email}</span>
                  </div>
                ))}
                {/* Newly selected — removable chips */}
                {selectedUsers
                  .filter(u => !recipients.some(r => r.recipient_email === u.user_email))
                  .map(u => (
                    <div
                      key={u.user_email}
                      className="flex items-center gap-1 pl-2 pr-1.5 py-0.5 rounded-full text-[12px] font-medium border"
                      style={{ borderColor: primaryColor, color: primaryColor, backgroundColor: `${primaryColor}12` }}
                    >
                      <span className="truncate max-w-[120px]">{u.user_fullname || u.user_email}</span>
                      <button onClick={() => toggleUser(u)} className="hover:opacity-60"><X size={10} /></button>
                    </div>
                  ))
                }
              </div>
            )}

            <div className="flex gap-2">
              {/* Search input */}
              <div className="relative flex-1">
                <input
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
                  placeholder={t('share.searchPlaceholder')}
                  className="w-full px-3 py-2 text-[13px] rounded-lg outline-none transition-all"
                  style={{ border: `1.5px solid ${searchFocused ? primaryColor : '#E5E7EB'}`, backgroundColor: '#F9FAFB' }}
                />
                {searchFocused && query.trim() && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#E5E7EB] rounded-xl shadow-lg z-[999] max-h-48 overflow-y-auto thin-scrollbar">
                    {isSearching ? (
                      <div className="py-4 text-center text-[13px] text-[#9CA3AF]">…</div>
                    ) : searchResults.length === 0 ? (
                      <div className="py-4 text-center text-[13px] text-[#9CA3AF]">{t('share.noResults')}</div>
                    ) : searchResults.map(u => {
                      const isAlreadyShared = recipients.some(r => r.recipient_email === u.user_email)
                      const isNewlySelected = selectedUsers.some(su => su.user_email === u.user_email)
                      return (
                        <button
                          key={u.user_email}
                          onMouseDown={e => { e.preventDefault(); toggleUser(u); }}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 transition-colors text-left ${isAlreadyShared ? 'cursor-default opacity-60' : 'hover:bg-[#F9FAFB]'}`}
                        >
                          <Avatar name={u.user_fullname} email={u.user_email} size={28} color={primaryColor} />
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-medium text-[#111827] truncate">{u.user_fullname}</p>
                            <p className="text-[12px] truncate" style={{ color: isAlreadyShared ? primaryColor : '#6B7280' }}>
                              {isAlreadyShared ? t('share.alreadyShared') : u.user_email}
                            </p>
                          </div>
                          {(isAlreadyShared || isNewlySelected) && (
                            <Check size={13} style={{ color: primaryColor }} className="flex-shrink-0" />
                          )}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Send Invite button — only active when NEW users are selected */}
              <button
                onClick={handleSendInvite}
                disabled={!selectedUsers.some(u => !recipients.some(r => r.recipient_email === u.user_email)) || isSending}
                className="px-4 py-2 text-[13px] font-semibold text-white rounded-lg transition-colors disabled:opacity-40 flex-shrink-0 flex items-center gap-2 min-w-[110px] justify-center"
                style={{ backgroundColor: primaryColor }}
              >
                {isSending ? (
                  <>
                    <svg className="animate-spin h-3.5 w-3.5 text-white flex-shrink-0" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                    </svg>
                    <span>{t('share.sendInvite')}</span>
                  </>
                ) : t('share.sendInvite')}
              </button>
            </div>

            {sendError && <p className="mt-2 text-[12px] text-[#EF4444]">{sendError}</p>}
          </div>

          {/* ── Who can view section ───────────────────────────────── */}
          <div>
            <p className="text-[13px] font-semibold text-[#111827] mb-2.5">{t('share.whoCanView')}</p>

            <div className="border border-[#E5E7EB] rounded-xl overflow-visible">
              {/* Owner row */}
              <div className={rowCls}>
                <Avatar name={currentUser?.name} email={currentUser?.email} size={36} color={primaryColor} />
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-[#111827] truncate">{currentUser?.name || currentUser?.email || '—'}</p>
                  <p className="text-[12px] text-[#9CA3AF]">{t('share.youLabel')}</p>
                </div>
                <span
                  className="text-[11px] font-semibold px-2 py-1 rounded-md flex-shrink-0"
                  style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
                >
                  {t('history.accessOwner')}
                </span>
              </div>

              {/* Preview recipients */}
              {loadingRecipients ? (
                <div className="py-4 text-center text-[13px] text-[#9CA3AF]">…</div>
              ) : previewRecipients.length === 0 ? (
                <div className="px-4 py-3 text-[13px] text-[#9CA3AF]">{t('share.noRecipients')}</div>
              ) : (
                previewRecipients.map(r => (
                  <RecipientRow key={r.recipient_email} r={r} showMenu={true} />
                ))
              )}
            </div>

            {/* View All link */}
            {recipients.length > 0 && (
              <button
                onClick={() => setView('allViewers')}
                className="mt-2 text-[13px] font-medium underline underline-offset-2 transition-opacity hover:opacity-70"
                style={{ color: primaryColor }}
              >
                {t('share.viewAll')}
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#F3F4F6] flex-shrink-0 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 text-[13px] font-semibold text-[#374151] border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
          >
            {t('share.cancel')}
          </button>
        </div>
      </div>
    </div>
  )

  // ════════════════════════════════════════════════════════════════════════════
  //  ALL VIEWERS VIEW
  // ════════════════════════════════════════════════════════════════════════════
  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[520px] mx-4 max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0">
          <h2 className="text-[18px] font-bold text-[#111827]">{t('share.allViewers')}</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-[#374151] hover:bg-[#F9FAFB] transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 pb-2 flex-shrink-0">
          <p className="text-[13px] text-[#6B7280] mb-4">{t('share.allViewersDesc')}</p>

          {/* Search + count */}
          <div className="flex items-center gap-3 border border-[#E5E7EB] rounded-xl px-3 py-2 bg-[#F9FAFB]">
            <Search size={14} className="text-[#9CA3AF] flex-shrink-0" />
            <input
              type="text"
              value={viewerSearch}
              onChange={e => setViewerSearch(e.target.value)}
              placeholder={t('common.search') || 'Suchen...'}
              className="flex-1 text-[13px] bg-transparent outline-none"
            />
            <span className="text-[12px] text-[#9CA3AF] flex-shrink-0 whitespace-nowrap">
              {t('share.nPeople', { count: totalPeople })}
            </span>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto thin-scrollbar px-6 py-2">
          <div className="border border-[#E5E7EB] rounded-xl overflow-visible">
            {/* Owner row */}
            <div className={rowCls}>
              <Avatar name={currentUser?.name} email={currentUser?.email} size={36} color={primaryColor} />
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-[#111827] truncate">{currentUser?.name || currentUser?.email || '—'}</p>
                {currentUser?.email && <p className="text-[12px] text-[#9CA3AF] truncate">{currentUser?.email}</p>}
              </div>
              <span
                className="text-[11px] font-semibold px-2 py-1 rounded-md flex-shrink-0"
                style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
              >
                {t('history.accessOwner')}
              </span>
              <button
                className="ml-2 flex items-center gap-1 px-3 py-1.5 text-[12px] font-medium text-[#374151] border border-[#E5E7EB] rounded-lg opacity-50 cursor-default flex-shrink-0"
                tabIndex={-1}
              >
                {t('share.viewButton')} <ChevronDown size={12} />
              </button>
            </div>

            {filteredRecipients.length === 0 && viewerSearch.trim() ? (
              <div className="px-4 py-4 text-[13px] text-center text-[#9CA3AF]">{t('share.noResults')}</div>
            ) : (
              filteredRecipients.map(r => (
                <RecipientRow key={r.recipient_email} r={r} showMenu={true} fullLayout={true} />
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#F3F4F6] flex-shrink-0 flex justify-between">
          <button
            onClick={() => setView('main')}
            className="px-5 py-2 text-[13px] font-semibold text-[#374151] border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
          >
            {t('common.back')}
          </button>
          <button
            onClick={onClose}
            className="px-5 py-2 text-[13px] font-semibold text-white rounded-lg transition-colors"
            style={{ backgroundColor: primaryColor }}
          >
            {t('share.done')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ShareModal
